import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from 'shared/components/ui/alert-dialog';
import { AmountInput } from 'shared/components/ui/AmountInput';
import { Label } from 'shared/components/ui/label';
import { IconNote } from 'shared/components/ui/IconNote';
import { AssistantService } from 'shared/services/AssistantService';
import { ReportSectionsTree, ReportSections } from './ReportSectionsTree';
import { FinecoButton } from 'pages/assistant/components/FinecoButton';
import colorsStore from 'shared/store/colorsStore';
import useThreadsStore from 'shared/store/threadsStore';
import { createLogger } from 'shared/logger';

const log = createLogger('GenerateReport');

type GenerateReportProps = {
  artifact: {
    financial_plan: number;
    risk_profile: string;
  };
  threadId?: string;
  portfolioId?: string;
  portfolioData?: any;
  countervalueTotal?: number;
  commentoAlPortafoglio?: boolean;
  children: React.ReactNode;
  onGenerate: (data: any, fromReport?: boolean) => void;
  disabled?: boolean;
};

export const GenerateReport = forwardRef<{ generate: () => void }, GenerateReportProps>(
  (
    {
      artifact,
      threadId,
      portfolioId,
      portfolioData,
      countervalueTotal,
      commentoAlPortafoglio,
      children,
      onGenerate,
      disabled,
    },
    ref,
  ) => {
    const colorsConfig = colorsStore((state) => state.colorsConfigStore);

    const { t } = useTranslation();
    const [bankerName, setBankerName] = useState('');
    const [bankerRole, setBankerRole] = useState('');
    const hasCountervalue = countervalueTotal != null && countervalueTotal > 0;
    const [proposedValue, setProposedValue] = useState<number | undefined>(
      hasCountervalue ? countervalueTotal : undefined,
    );
    const [reportSections, setReportSections] = useState<ReportSections>({});
    const [showCosts, setShowCosts] = useState(true);
    const [showIsin, setShowIsin] = useState(true);

    // Sync proposedValue when countervalueTotal changes (late prop arrival)
    useEffect(() => {
      if (countervalueTotal != null && countervalueTotal > 0) {
        setProposedValue(countervalueTotal);
      }
    }, [countervalueTotal]);

    // Access story from global store
    const stories = useThreadsStore((state) => state.stories);
    const fetchStory = useThreadsStore((state) => state.fetchStory);

    // Polling ref
    const pollingRef = useRef<NodeJS.Timeout | null>(null);

    // Helper to check if portfolio_story section is selected
    const isPortfolioStorySelected = () => {
      return reportSections.portfolio_story?.visible && reportSections.portfolio_story?.selected;
    };

    // Helper to get current story from the global store
    const getCurrentStory = () => {
      if (!threadId || !portfolioId) return null;
      return stories?.[threadId]?.[portfolioId] ?? null;
    };

    // Helper to check if story is available (null/undefined = still loading, anything else = done)
    const isStoryAvailable = () => {
      const story = getCurrentStory();
      // Story is available when it's not null/undefined (empty string or actual story = done loading)
      return story !== null && story !== undefined;
    };

    // Check if PDF generation should be disabled
    // Only disable if commentoAlPortafoglio is true, portfolio_story is selected, and story not available
    const isPdfGenerationDisabled = () => {
      return commentoAlPortafoglio && isPortfolioStorySelected() && !isStoryAvailable();
    };

    useImperativeHandle(ref, () => ({
      generate: () => {
        handleGenerate(false);
      },
    }));

    useEffect(() => {
      const loadReportParameters = async () => {
        try {
          let response: any;

          if (portfolioData) {
            response = await AssistantService.fetchReportParameters(portfolioId);
          } else {
            log.warn('no portfolio data / ids for report parameters');
            return;
          }

          setBankerName(response.banker_name || '');
          setBankerRole(response.banker_role || '');
          setShowCosts(response.show_instrument_costs ?? true);
          setShowIsin(response.show_isin ?? true);

          // Process sections to add hidden flag
          const processedSections = JSON.parse(JSON.stringify(response.report_sections));
          setReportSections(processedSections);
        } catch (error) {
          log.error('load report parameters failed', { error });
        }
      };

      loadReportParameters();
    }, [threadId, portfolioId, portfolioData]);

    // Start/stop polling when portfolio_story selection changes
    // Only poll if commentoAlPortafoglio is true (feature enabled for this portfolio)
    useEffect(() => {
      const shouldPoll = commentoAlPortafoglio && isPortfolioStorySelected() && !isStoryAvailable();

      if (shouldPoll && threadId && portfolioId) {
        // Initial fetch
        fetchStory(threadId, portfolioId);

        // Start polling every 5 seconds
        pollingRef.current = setInterval(() => {
          fetchStory(threadId, portfolioId);
        }, 5000);
      }

      return () => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      };
    }, [commentoAlPortafoglio, reportSections.portfolio_story?.selected, threadId, portfolioId, stories]);

    const handleGenerate = (fromReport: boolean = false) => {
      const data = {
        banker_name: bankerName,
        banker_role: bankerRole,
        financial_plan: artifact?.financial_plan?.toString(),
        mifid_profile: artifact?.risk_profile,
        proposed_value: proposedValue,
        show_instrument_costs: showCosts,
        show_isin: showIsin,
        ...(fromReport && { report_sections: reportSections }),
      };

      onGenerate(data, fromReport);
    };

    const hasSelectedSections = () => {
      return Object.values(reportSections).some((section) => section.visible && section.selected);
    };

    const [open, setOpen] = useState(false);
    return (
      <AlertDialog
        open={open}
        onOpenChange={(isOpen) => {
          if (disabled && isOpen) return;
          setOpen(isOpen);
          if (!isOpen && pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        }}
      >
        <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
        <AlertDialogContent
          style={{
            backgroundColor: 'white',
            color: colorsConfig.threadHistoryCopy || '#ffffff',
            maxWidth: '600px',
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: colorsConfig.finecoBlack }}>{t('generateReport.title')}</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="bankerRole" style={{ color: colorsConfig.finecoBlack }}>
                    {t('generateReport.bankerRole')}
                  </Label>
                  <select
                    id="bankerRole"
                    value={bankerRole}
                    onChange={(e) => setBankerRole(e.target.value)}
                    className="w-full rounded border p-2"
                    style={{ color: colorsConfig.finecoBlack }}
                  >
                    <option value="Personal Financial Advisor">Personal Financial Advisor</option>
                    <option value="Private Banker">Private Banker</option>
                    <option value="Senior Private Banker">Senior Private Banker</option>
                    <option value="Senior Private Banker e Ambassador">Senior Private Banker e Ambassador</option>
                    <option value="Group Manager">Group Manager</option>
                    <option value="Area Manager">Area Manager</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="proposedValue" style={{ color: colorsConfig.finecoBlack }}>
                    {hasCountervalue ? t('generateReport.totalPortfolioValue') : t('generateReport.proposedValue')}
                  </Label>
                  <AmountInput
                    value={proposedValue || ''}
                    onChangeValue={(v) => setProposedValue(parseFloat(v) || 0)}
                    className="h-10 w-full rounded-xl border px-3 py-2 text-sm text-right"
                    disabled={hasCountervalue}
                    style={{
                      backgroundColor: hasCountervalue ? '#f5f5f5' : colorsConfig.white,
                      color: colorsConfig.finecoBlack,
                      borderColor: colorsConfig.gray2,
                      ...(hasCountervalue && { cursor: 'not-allowed' }),
                    }}
                  />
                </div>
              </div>

              <div className="mt-8">
                <h3 className="mb-4 font-medium" style={{ color: colorsConfig.finecoBlack }}>
                  {t('generateReport.sections')}
                </h3>
                <ReportSectionsTree
                  sections={reportSections}
                  onChange={setReportSections}
                  showCosts={showCosts}
                  onShowCostsChange={setShowCosts}
                  showIsin={showIsin}
                  onShowIsinChange={setShowIsin}
                />

                {/* Story status message */}
                {isPortfolioStorySelected() && (
                  <div className="mt-4">
                    {isPdfGenerationDisabled() ? (
                      <IconNote
                        type="attention"
                        title={t('generateReport.storyGenerating')}
                        body={t('generateReport.storyGeneratingDescription')}
                      />
                    ) : (
                      <IconNote
                        type="important"
                        title={t('generateReport.storyAvailable')}
                        body={t('generateReport.storyAvailableDescription')}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </AlertDialogDescription>
          <AlertDialogFooter>
            <FinecoButton
              click={() => {
                setOpen(false);
              }}
              variant="white"
              copy={t('global.cancel').toUpperCase()}
            />
            <FinecoButton
              disabled={!hasSelectedSections() || isPdfGenerationDisabled()}
              click={() => {
                handleGenerate(true);
                setOpen(false);
              }}
              copy={
                isPdfGenerationDisabled()
                  ? t('generateReport.waitingForStory').toUpperCase()
                  : t('generateReport.generate').toUpperCase()
              }
            />
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  },
);
